import type { Theme } from 'foundations-theming-base/types';
import {
  getThemeToProvide,
  getThemeToProvideSync,
  isSyncThemeSettings,
  trackTheme,
} from '../components/ThemeProvider/ThemeProvider.helpers';
import {
  DEFAULT_SETTINGS,
  ThemeSettings,
  getLocalThemeSettings,
  setTrellisEnabledFlag,
} from './getLocalThemeSettings';
import devLogger from 'react-utils/devLogger';

export const TRELLIS_THEME_EVENT = 'trellis:theme';

export class ThemeManager {
  private themeRequestCounter = 0;
  theme: Theme;
  subscribers = new Set<() => void>();

  constructor() {
    const themeSettings = getLocalThemeSettings();
    this.theme = getThemeToProvideSync(themeSettings);
    this.handleUpdate(themeSettings);
  }

  setThemeObject = (theme: Theme) => {
    this.theme = theme;
    this.subscribers.forEach((callback) => callback());
  };

  /**
   * Allow the theme manager to read the `trellis:theme` setting from localStorage, and update the theme object.
   */
  setTrellisEnabled(value: boolean) {
    setTrellisEnabledFlag(value);
    const themeSettings = getLocalThemeSettings();
    this.handleUpdate(themeSettings);
  }

  subscribeToWindowEvents = () => {
    window.addEventListener('message', this.handleUpdateEvent);
  };

  destroy = () => {
    window.removeEventListener('message', this.handleUpdateEvent);
  };

  handleUpdateEvent = (event: MessageEvent) => {
    if (event?.data?.type !== TRELLIS_THEME_EVENT) {
      return;
    }
    const newThemeSettings = getLocalThemeSettings();
    this.handleUpdate(newThemeSettings);
  };

  handleUpdate = (newThemeSettings: ThemeSettings) => {
    trackTheme(newThemeSettings.themeName, newThemeSettings.themeMode);

    this.themeRequestCounter += 1;
    const currentRequestId = this.themeRequestCounter;

    if (isSyncThemeSettings(newThemeSettings)) {
      const syncTheme = getThemeToProvideSync(newThemeSettings);
      this.setThemeObject(syncTheme);
    } else {
      getThemeToProvide(newThemeSettings)
        .then((theme) => {
          if (currentRequestId === this.themeRequestCounter) {
            this.setThemeObject(theme);
          }
        })
        .catch((error) => {
          devLogger.warn({
            message: `Failed to load theme with error: ${error}`,
            key: 'foundations-theming:theme-manager:theme-load-failure',
          });
        });
    }
  };

  subscribe = (callback: () => void): (() => void) => {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  };

  getTheme = (): Theme => {
    return this.theme;
  };

  setTheme = (themeSettings: Partial<ThemeSettings>) => {
    const newThemeSettings: ThemeSettings = {
      ...DEFAULT_SETTINGS,
      ...themeSettings,
    };
    this.handleUpdate(newThemeSettings);
  };
}

export let globalThemeManager: ThemeManager;
export function createGlobalThemeManager() {
  if (globalThemeManager) {
    globalThemeManager.destroy();
  }
  globalThemeManager = new ThemeManager();
  globalThemeManager.subscribeToWindowEvents();
}
createGlobalThemeManager();
